import { useState, useEffect, useRef } from 'react';
import { useSettings, BankAccount } from '@/contexts/SettingsContext';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Upload, X, CheckCircle2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

export type PaymentMethod = 'bankTransfer' | 'cashOnDelivery' | 'manualPayment' | 'whatsapp';

interface PaymentMethodSelectProps {
  onSelectMethod: (method: PaymentMethod, paymentDetails?: { proofOfPayment?: File, notes?: string }) => void;
  className?: string;
  paymentOptions?: {
    bankTransfer?: { enabled?: boolean; accountInfo?: string; accounts?: BankAccount[] };
    cashOnDelivery?: { enabled?: boolean };
    manualPayment?: { enabled?: boolean; instructions?: string };
  };
  showWhatsApp?: boolean;
}

export function PaymentMethodSelect({ 
  onSelectMethod, 
  className,
  paymentOptions: propPaymentOptions,
  showWhatsApp = false
}: PaymentMethodSelectProps) {
  const { checkoutOptions } = useSettings();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | ''>('');
  const [paymentDetails, setPaymentDetails] = useState<{
    proofOfPayment?: File;
    notes?: string;
  }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Get payment options from props or settings
  const paymentOptions = propPaymentOptions || checkoutOptions.paymentOptions || {
    bankTransfer: { enabled: false, accountInfo: '', accounts: [] },
    cashOnDelivery: { enabled: false },
    manualPayment: { enabled: false, instructions: '' }
  };
  const bankAccounts = paymentOptions.bankTransfer?.accounts ?? [];

  
  // Set default payment method based on available options
  useEffect(() => {
    if (showWhatsApp) {
      setSelectedMethod('whatsapp');
    } else if (paymentOptions.bankTransfer?.enabled) {
      setSelectedMethod('bankTransfer');
    } else if (paymentOptions.cashOnDelivery?.enabled) {
      setSelectedMethod('cashOnDelivery');
    } else if (paymentOptions.manualPayment?.enabled) {
      setSelectedMethod('manualPayment');
    }
  }, [paymentOptions, showWhatsApp]);
  
  // Notify parent component when method changes
  useEffect(() => {
    if (selectedMethod) {
      onSelectMethod(selectedMethod, paymentDetails);
    }
  }, [selectedMethod, paymentDetails, onSelectMethod]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  
  // Don't render if no payment options are enabled
  const hasEnabledPaymentOptions = 
    showWhatsApp ||
    paymentOptions.bankTransfer?.enabled || 
    paymentOptions.cashOnDelivery?.enabled || 
    paymentOptions.manualPayment?.enabled;
    
  if (!hasEnabledPaymentOptions) {
    return (
      <div className="p-4 bg-muted/30 rounded-md">
        <p className="text-sm text-muted-foreground">No payment options are currently available. Please contact the store.</p>
      </div>
    );
  }

  const handleFileUpload = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setPaymentDetails(prev => ({ 
        ...prev, 
        proofOfPayment: file 
      }));
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setPaymentDetails(prev => ({ 
      ...prev, 
      proofOfPayment: undefined 
    }));
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPaymentDetails(prev => ({ 
      ...prev, 
      notes: e.target.value
    }));
  };
  
  return (
    <div className={cn("space-y-4", className)}>
      <h3 className="text-base font-medium">Payment Method</h3>
      
      <RadioGroup 
        value={selectedMethod} 
        onValueChange={(value) => setSelectedMethod(value as PaymentMethod)}
        className="space-y-3"
      >
        {showWhatsApp && (
          <div className="space-y-3 rounded-md border p-4 bg-teal-50">
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="whatsapp" id="whatsapp" />
              <Label htmlFor="whatsapp" className="font-medium cursor-pointer flex items-center gap-2">
               
                WhatsApp Checkout
              </Label>
            </div>
            
            {selectedMethod === 'whatsapp' && (
              <div className="ml-6 pt-2">
                <div className="bg-white p-3 rounded-md text-sm">
                  <p className="font-medium text-teal-800 mb-1">Quick and Easy!</p>
                  <p className="text-teal-700">
                    Complete your order directly via WhatsApp. Your order details will be sent to the store for confirmation.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {paymentOptions.bankTransfer?.enabled && (
          <div className="space-y-3 rounded-md border p-4 bg-white">
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="bankTransfer" id="bankTransfer" />
              <Label htmlFor="bankTransfer" className="font-medium cursor-pointer">
                Bank Transfer
              </Label>
            </div>
            
            {selectedMethod === 'bankTransfer' && (
              <div className="ml-6 pt-2 space-y-3">
                <div className="bg-white p-3 rounded-md text-sm space-y-2">
                  <p className="mb-1 font-medium">Bank Account Information</p>
                  {bankAccounts.length > 0 ? (
                    <div className="space-y-2">
                      {bankAccounts.map((account, index) => (
                        <div key={`${account.accountNumber}-${index}`} className="rounded border border-dashed border-border/60 p-2">
                          <p className="font-semibold">{account.accountName}</p>
                          <p className="text-sm text-muted-foreground">Account Number: {account.accountNumber}</p>
                          <p className="text-sm text-muted-foreground">Bank: {account.bankName}</p>
                        </div>
                      ))}
                    </div>
                  ) : paymentOptions.bankTransfer?.accountInfo ? (
                    <pre className="whitespace-pre-wrap">{paymentOptions.bankTransfer.accountInfo}</pre>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      This store has enabled bank transfer but hasn't provided account details yet. Please contact them to confirm.
                    </p>
                  )}
                </div>
                
                {paymentOptions.bankTransfer?.accountInfo && bankAccounts.length > 0 && (
                  <div className="bg-muted/10 p-3 rounded-md text-xs text-muted-foreground">
                    {paymentOptions.bankTransfer.accountInfo}
                  </div>
                )}
                
                <div className="space-y-3">
                  <Label htmlFor="paymentProof" className="text-sm font-medium">
                    Upload Payment Proof (Screenshot)
                  </Label>
                  
                  {!paymentDetails.proofOfPayment ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        "relative border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer",
                        "hover:border-primary hover:bg-primary/5",
                        isDragging && "border-primary bg-primary/10 scale-[1.02]",
                        !isDragging && "border-muted-foreground/25"
                      )}
                    >
                      <div className="flex flex-col items-center justify-center gap-3 text-center">
                        <div className={cn(
                          "rounded-full p-3 transition-colors",
                          isDragging ? "bg-primary/20" : "bg-muted"
                        )}>
                          <Upload className={cn(
                            "h-6 w-6 transition-colors",
                            isDragging ? "text-primary" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {isDragging ? 'Drop your image here' : 'Click to upload or drag and drop'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            PNG, JPG, JPEG up to 10MB
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative rounded-lg border bg-muted/30 p-4">
                      <div className="flex items-start gap-4">
                        {previewUrl && (
                          <div className="relative flex-shrink-0">
                            <img
                              src={previewUrl}
                              alt="Payment proof preview"
                              className="h-20 w-20 rounded-md object-cover border"
                            />
                            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5">
                              <CheckCircle2 className="h-3 w-3 text-white" />
                            </div>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {paymentDetails.proofOfPayment.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {formatFileSize(paymentDetails.proofOfPayment.size)}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile();
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    id="paymentProof"
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  
                  <p className="text-xs text-muted-foreground">
                    Please upload a clear screenshot of your payment transaction for verification.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {paymentOptions.cashOnDelivery?.enabled && (
          <div className="space-y-3 rounded-md border p-4 bg-white">
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="cashOnDelivery" id="cashOnDelivery" />
              <Label htmlFor="cashOnDelivery" className="font-medium cursor-pointer">
                Cash on Delivery
              </Label>
            </div>
            
            {selectedMethod === 'cashOnDelivery' && (
              <div className="ml-6 pt-2">
                <div className="bg-white p-3 rounded-md text-sm">
                  <p>You'll pay when your order is delivered to your location.</p>
                </div>
                
                <div className="mt-3 space-y-2">
                  <Label htmlFor="cashNotes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="cashNotes"
                    placeholder="Any specific instructions regarding payment..."
                    onChange={handleNotesChange}
                    className="resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        )}
        
       
      </RadioGroup>
    </div>
  );
} 